@let corporateDesign = corporateDesignService.corporateDesign();
@let texts = textStore.texts();
@let loading =
  corporateDesignService.isCorporateDesignLoading() ||
  onboardingStore.isLoading();

<aw-dialog
  (onClose)="dialogRef.close($event)"
  [content]="content"
  [footer]="footer"
  [data]="data"
  [width]="'min(1000px, 96vw)'"
  [title]="
    texts
      | filterTexts: ERelationType.onboardingTitle : EEntityType.registration
      | language
  "
  [fontSize]="corporateDesign.fontSizeTitle | toFontSize"
  [closingColor]="corporateDesignService.primaryColor()">
  <ng-template #content>
    @if (loading) {
      <div class="pt-32 pb-32">
        <aw-loading-animation
          [entityType]="EEntityType.registration"
          [relationType]="ERelationType.loading" />
      </div>
    } @else {
      <form
        id="user-onboarding-form"
        class="flex flex-col gap-10"
        [style.font-size]="corporateDesign.fontSizeText | toFontSize"
        [style.color]="corporateDesignService.fontColor()"
        [formGroup]="onboardingForm"
        (ngSubmit)="completeOnboarding()">
        @if (isExistingCustomerRegistrationActive()) {
          <div class="flex w-full">
            <aw-button-group
              class="w-full"
              formControlName="customerTypeSelection"
              id="onboarding-customer-type"
              [options]="ERegisterOptions | enumToButtonOptionArray"
              [buttonsPerRow]="2" />
          </div>
        }

        <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
          <section class="flex min-w-0 flex-col gap-3">
            <h3 class="text-2xl">
              {{
                texts
                  | filterTexts
                    : ERelationType.personalDataHeading
                    : EEntityType.generalArea
                  | language
              }}
            </h3>

            <aw-drop-down
              id="onboarding-salutation"
              [options]="salutationOptions() | toTranslatedOption"
              [label]="
                texts
                  | filterTexts
                    : ERelationType.newCustomerSalutation
                    : EEntityType.generalArea
                  | language
              "
              formControlName="salutation" />

            <aw-text-input
              id="onboarding-first-name"
              [label]="
                texts
                  | filterTexts
                    : ERelationType.newCustomerFirstName
                    : EEntityType.generalArea
                  | language
              "
              formControlName="firstName" />

            <aw-text-input
              id="onboarding-last-name"
              [label]="
                texts
                  | filterTexts
                    : ERelationType.newCustomerLastName
                    : EEntityType.generalArea
                  | language
              "
              formControlName="lastName" />

            <aw-date-input
              id="onboarding-birth-date"
              [max]="earliestValidBirthDay()"
              [label]="
                texts
                  | filterTexts
                    : ERelationType.personalDataBirthdate
                    : EEntityType.generalArea
                  | language
              "
              formControlName="birthDate" />

            <aw-text-input
              id="onboarding-phone-number"
              [label]="
                texts
                  | filterTexts
                    : ERelationType.newCustomerTelephone
                    : EEntityType.generalArea
                  | language
              "
              formControlName="phoneNumber" />
          </section>

          <section class="flex min-w-0 flex-col gap-3">
            <h3 class="text-2xl">
              {{
                texts
                  | filterTexts
                    : ERelationType.addressDataHeading
                    : EEntityType.generalArea
                  | language
              }}
            </h3>

            <aw-drop-down
              id="onboarding-country"
              name="name"
              valueAttribute="isoCode"
              [isLoading]="countryStore.isLoading()"
              [options]="availableCountries()"
              [label]="
                texts
                  | filterTexts
                    : ERelationType.addressDataCountry
                    : EEntityType.generalArea
                  | language
              "
              formControlName="country" />

            <div class="grid grid-cols-3 gap-2">
              <aw-text-input
                id="onboarding-post-code"
                [label]="
                  texts
                    | filterTexts
                      : ERelationType.addressDataPostalCode
                      : EEntityType.generalArea
                    | language
                "
                formControlName="postCode">
                {{
                  texts
                    | filterTexts
                      : ERelationType.postalCodeError
                      : EEntityType.generalArea
                    | language
                }}
              </aw-text-input>

              @if (useFreeAddressInput()) {
                <aw-text-input
                  class="col-span-2"
                  id="onboarding-city-free"
                  [label]="
                    texts
                      | filterTexts
                        : ERelationType.addressDataCity
                        : EEntityType.generalArea
                      | language
                  "
                  formControlName="city" />
              } @else {
                <aw-auto-complete
                  class="col-span-2"
                  id="onboarding-city"
                  name="city"
                  valueAttribute="city"
                  [isLoading]="cityStore.isLoading()"
                  [options]="availableCities()"
                  [useLooseValidation]="useLooseAddressValidation()"
                  [label]="
                    texts
                      | filterTexts
                        : ERelationType.addressDataCity
                        : EEntityType.generalArea
                      | language
                  "
                  formControlName="city" />
              }
            </div>

            <div class="grid grid-cols-4 gap-2">
              @if (useFreeAddressInput()) {
                <aw-text-input
                  class="col-span-3"
                  id="onboarding-street-free"
                  [label]="
                    texts
                      | filterTexts
                        : ERelationType.addressDataStreet
                        : EEntityType.generalArea
                      | language
                  "
                  formControlName="street" />
              } @else {
                <aw-auto-complete
                  class="col-span-3"
                  id="onboarding-street"
                  name="street"
                  valueAttribute="street"
                  [isLoading]="streetStore.isLoading()"
                  [options]="availableStreets()"
                  [useLooseValidation]="useLooseAddressValidation()"
                  [label]="
                    texts
                      | filterTexts
                        : ERelationType.addressDataStreet
                        : EEntityType.generalArea
                      | language
                  "
                  formControlName="street" />
              }

              <aw-text-input
                id="onboarding-house-number"
                [label]="
                  texts
                    | filterTexts
                      : ERelationType.addressDataHouseNumber
                      : EEntityType.generalArea
                    | language
                "
                formControlName="houseNumber" />
            </div>

            <aw-text-input
              id="onboarding-additional"
              [label]="
                texts
                  | filterTexts
                    : ERelationType.addressDataExtra
                    : EEntityType.generalArea
                  | language
              "
              formControlName="additional" />

            @if (showAddressConfirmationBox()) {
              <aw-checkbox
                id="onboarding-address-confirmation"
                [label]="
                  texts
                    | filterTexts
                      : ERelationType.confirmation
                      : EEntityType.generalArea
                    | language
                "
                formControlName="confirmAddressIsAccurate" />
            }

            @if (showAddressWarning()) {
              <div class="text-input-error text-base" role="alert">
                {{
                  texts
                    | filterTexts
                      : ERelationType.warning
                      : EEntityType.generalArea
                    | language
                }}
              </div>
            }
          </section>
        </div>

        <aw-checkbox
          id="onboarding-terms-accepted"
          [label]="
            texts
              | filterTexts: ERelationType.registryTos : EEntityType.generic
              | language
          "
          formControlName="termsAccepted" />
      </form>
    }
  </ng-template>

  <ng-template #footer>
    <aw-button
      class="w-full"
      id="complete-onboarding"
      type="submit"
      form="user-onboarding-form"
      [isDisabled]="!isFormReady()"
      [text]="
        texts
          | filterTexts
            : ERelationType.onboardingCompleteButton
            : EEntityType.registration
          | language
      "
      (click)="completeOnboarding()" />
  </ng-template>
</aw-dialog>
